import { getCurrentLanguage } from '@eureka/language-tool';
import { addTrialService } from '@eureka/services/src/add-trial-service';
import { isAnonymousError } from '@eureka/services/src/exception';
import { isStartupAutoReloading, markStartupAutoReloadSettled, startupAutoReload } from '@eureka/services/src/startup-auto-reload';
import { accountDeferred, prepareTokenDeferred, setPrepareTokenDeferred } from '@eureka/utils/account';
import { controlDeferred } from '@eureka/utils/control';
import { REGION } from '@eureka/utils/environment';
import { DeployEnv, HeaderService, SLD, Site } from '@patsnap-utils/biz';
import { handleLogout } from '@patsnap-utils/passport';
import psHttp, { generateExceptionRedirector, psAxios } from '@patsnap/common-ps-http';
import { createCreditsChargeSdk } from '@patsnap/credits-charge';
import { createUniformSetting } from '@patsnap/uniform-setting';
import type { T_Locale, T_Region } from '@patsnap/uniform-setting/dist/locale/types';
import type { AxiosInstance } from 'axios';
import _ from 'lodash';
import browser from './browser';
import setWebsiteFont from './configure-website-font';
import { customProductsLinkInUserCenter, type CustomLinksOptions } from './custom-links';
import { type IProjectPreset, _passportDeferred, passportInterceptManager, prepareTokenBase, updateTokenBaseRequestHeaders } from './enable-token-base';
import { removeEurekaLoginCookie, setEurekaLoginCookie } from './set-eureka-login-cookie';
import { clientDeferred } from '@eureka/utils/client';

const supports: T_Locale[] = ['cn', 'en', 'jp', 'tw']
export interface IStartupOption {
    headerService?: HeaderService
    projectName: string
    preset?: IProjectPreset
    pageInit: () => Promise<unknown>
    /**
     * 禁用自动赋权
     */
    disableTrial?: boolean
    /**
     * 禁止加载客服
     */
    disableCustomerService?: boolean,
    /**
     * 自定义链接选项
     */
    customLinksOptions?: CustomLinksOptions
}

const lang = getCurrentLanguage()
const creditsChargeEnabledRegions = [DeployEnv.US, DeployEnv.EU, DeployEnv.STAGE]

const { includes, filter, get } = _

async function checkAndClaimWelcomeCredits(locale: T_Locale) {
    if (!includes(creditsChargeEnabledRegions, REGION)) return

    try {
        const result = await createCreditsChargeSdk({
            baseURL: process.env.VUE_APP_EUREKA_SERVICE_URI,
            http: psHttp,
            locale: locale as 'cn' | 'en' | 'jp' | 'tw',
        }).checkAndClaim()

        if (result.status === 'claimed') {
            console.log(`[CreditsCharge] successfully claimed ${result.amount} credits`)
        } else if (result.status === 'error') {
            console.error('[CreditsCharge]', `failed during ${result.stage}`, result.error)
        }
    } catch (error) {
        console.error('[CreditsCharge]', 'failed to check and claim credits', error)
    }
}

export async function startup(startupOption: IStartupOption): Promise<unknown[]> {
    browser()
    const { isFeiShu } = await clientDeferred.promise
    if (isFeiShu) {
        startupOption.disableCustomerService = true
    }
    const promise = startupOption.preset
        ? prepareTokenBase(startupOption.preset).then((p) => {
            setEurekaLoginCookie();
            setPrepareTokenDeferred(p)
            /* send auth ready signal */
            return p;
        }).catch(e => {
            if (isAnonymousError(e)) {
                // 只有在startup的preset.preRedirectLogin中手动throw new AnonymousError才会触发
                passportInterceptManager.eject();
                accountDeferred.reject(e);
                prepareTokenDeferred.reject(e);
                _passportDeferred.reject(e);
            }
        })
        : Promise.resolve()
    // @ts-ignore
    return promise.then((p) => {
        const headerService = startupOption.headerService || new HeaderService()
        headerService.logoHref = new Site({ sld: SLD.EUREKA, pathname: 'home' }).href

        return Promise.all([
            startupOption.pageInit().catch(e => {
                if (!addTrialService.ENABLE_AUTO_ADD_TRIAL) return Promise.reject(e)
            }),
            // 游客和登录用户都需要调用 headerService.sync() 来初始化 Header 基础数据（如 locales）
            // 游客模式下，sync() 会发起 account-info 等请求并收到 401，但这些请求在 catch 中被忽略，不影响功能
            headerService.sync({ http: psHttp, uri: process.env.VUE_APP_ACCOUNT_SERVICE_URI as string }).finally(
                () => {
                    customProductsLinkInUserCenter(headerService, startupOption.customLinksOptions)
                    headerService.visibleProducts = !isFeiShu
                    // 只有登录用户才 resolve accountDeferred
                    if (p && headerService.account) {
                        accountDeferred.resolve(headerService.account);
                    }
                }
            ).then(() => {
                if (startupOption.disableTrial) {
                    return;
                }
                // 只有登录用户才执行 addTrial
                if (p) {
                    return addTrialService.addTrial(headerService, p)
                }
            }),
            (() => {
                const setting = createUniformSetting({
                    verbose: true,
                    axiosInstance: psAxios as AxiosInstance as any,
                    loggedIn: true,
                    baseAccountUrl: process.env.VUE_APP_ACCOUNT_SERVICE_URI,
                    locale: {
                        supports,
                    },
                    region: process.env.VUE_APP_DEPLOY_ENV as T_Region,
                })

                // @ts-ignore
                return setting.locale.getLocale().then((data) => {
                    const locale = data.user || data.default
                    setWebsiteFont(locale)
                    updateTokenBaseRequestHeaders({ 'X-Site-Lang': locale.toUpperCase() })
                    return { locale, setting }
                })
            })()
        ]).then((data) => {
            const [pageData, , { locale, setting }] = data

            /* 如果 cookies 中存储的语言和持久化的语言不匹配，则刷新页面 */
            if (lang !== locale && startupAutoReload('locale_mismatch', { lang, locale })) {
                return
            }

            /* WhiteLabel 时，隐藏 PatSnap Logo */
            if (headerService.whiteLabel) {
                headerService.logoVisible = false
            }

            const { locales } = headerService
            headerService.locales = filter(locales, locale => includes(supports, locale.key)).map(({ ...rest }) => {
                return {
                    ...rest,
                    active: rest.key === locale,
                }
            })

            /* 注册切换语言函数 */
            // eslint-disable-next-line @typescript-eslint/ban-types
            headerService.beforeLocaleChange = function beforeLocaleChange(localeKey: string, done: Function) {
                setting.locale.setLocale(localeKey as T_Locale).then(() => {
                    window.location.reload()
                    done()
                })
            }

            /* 注册登出回调函数 */
            // eslint-disable-next-line @typescript-eslint/ban-types
            headerService.userCenterService.logoutHandler = async function logout(done: Function, logoutHref: string, option: unknown) {
                handleLogout().then((data: unknown) => {
                    removeEurekaLoginCookie();
                    setTimeout(() => {
                        const { createLogoutHref } = option as { createLogoutHref: (appendRedirect?: boolean, query?: object) => string }
                        const url = createLogoutHref(true, { anonymousLogin: false })
                        const href = get(data, 'data.redirect_url', url);
                        window.location.href = href;
                    }, 1000)
                });
            }

            /** header头切换租户成功后页面跳转 */
            headerService.switchTenantHandler = async(tenantId, done) => {
                return done().then(() => {
                    window.location.href = new Site({ sld: SLD.EUREKA, pathname: 'home' }).href
                })
            }

            Promise.all([accountDeferred.promise, controlDeferred.promise]).then(([account, control]) => {
                startupOption.disableCustomerService = control.freemium
                if (account?.isLogin) {
                    import(/* webpackChunkName: "defer" */'./defer').then(({ defer }) => {
                        defer(startupOption, account, locale)
                    })
                }
            })

            if (!isStartupAutoReloading()) {
                markStartupAutoReloadSettled()
                if (p) {
                    checkAndClaimWelcomeCredits(locale)
                }
            }

            return [headerService, locale, pageData, setting]
        })
            .catch((error: Error) => {
                if (!addTrialService.ENABLE_AUTO_ADD_TRIAL) {
                    generateExceptionRedirector(403, () => {
                        window.location.href = new Site({ sld: SLD.EUREKA, pathname: '403/' }).href
                    })(error);
                }
            });
    })
}
